/**
 * useSparklineData Hook
 *
 * Lazily fetches 24h sparkline price data for a set of token mint addresses.
 * Designed to be enabled only when the user is in table/list view mode,
 * keeping sparkline data out of the critical path for grid view (the default).
 *
 * Uses a single Supabase query instead of N individual TradingView API calls.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

async function fetchSparklineData(
  mintAddresses: string[]
): Promise<Map<string, number[]>> {
  if (mintAddresses.length === 0) return new Map();

  const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

  const { data, error } = await supabase
    .from('token_price_history')
    .select('token_address, price_sol, recorded_at')
    .in('token_address', mintAddresses)
    .gte('recorded_at', oneDayAgo)
    .order('recorded_at', { ascending: true });

  if (error) {
    console.warn('[useSparklineData] Error fetching sparkline data:', error.message);
    return new Map();
  }

  const sparklineMap = new Map<string, number[]>();
  if (data) {
    for (const row of data) {
      const addr = (row as { token_address: string }).token_address;
      const price = (row as { price_sol: number }).price_sol;
      const existing = sparklineMap.get(addr) || [];
      existing.push(price);
      sparklineMap.set(addr, existing);
    }
  }

  return sparklineMap;
}

export function useSparklineData(mintAddresses: string[], enabled = true) {
  const sorted = [...mintAddresses].sort();

  const query = useQuery({
    queryKey: ['sparkline-data', ...sorted.slice(0, 5), sorted.length],
    queryFn: () => fetchSparklineData(mintAddresses),
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    enabled: enabled && mintAddresses.length > 0,
  });

  return {
    sparklines: query.data || new Map<string, number[]>(),
    isLoading: query.isLoading,
  };
}
